import { detect } from 'detect-browser';
import queryString from 'query-string';
import VueRouter from 'vue-router';
import { FormStage2SummaryItem } from '@informme/shared/schemas/form/FormStage2Schema';

export default class Utils {
  /** to get query from vue router in hash mode
   * queryString.parseUrl or .extract don't work in hash mode.
   */
  static getQuery(): { [key: string]: string } {
    const arr = window.location.hash.split('?');
    if (arr.length < 2) return {};
    const query = queryString.parse(arr[1]);

    return query as { [key: string]: string };
  }

  static isIOS() {
    const browser = detect();
    return browser?.os === 'iOS';
  }

  /** Get browser language with fallback to English */
  static getBrowserLanguage(): string {
    // Check localStorage first (user preference)
    const storedLang = localStorage.getItem('i18nLocale');
    if (storedLang) {
      return storedLang;
    }

    // Get browser language (navigator.language is standard, userLanguage is IE legacy)
    const browserLang = navigator.language || 'en';

    // Extract language code (e.g., 'en-US' -> 'en', 'de-DE' -> 'de')
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Return language code or fallback to 'en'
    return langCode || 'en';
  }

  /** Check if redirected parameter exists in route query or main URL query */
  /** Used to redirect from Stage 1 editor to Stage 2 editor */
  static isURLRedirected(routeQuery: Record<string, string | (string | null)[]>): boolean {
    const routeRedirected = routeQuery.redirected === 'true';
    const urlParams = queryString.parse(window.location.search);
    const urlRedirected = urlParams.redirected === 'true';

    return routeRedirected || urlRedirected;
  }

  /** Remove redirected parameter from URL to prevent showing alert on bookmark */
  static removeRedirectedFromURL(router: VueRouter): void {
    // Remove from route query
    const newQuery = { ...router.currentRoute.query };
    delete newQuery.redirected;
    router.replace({ query: newQuery }).catch(() => {
      // Ignore navigation errors
    });

    // Remove from main URL query (before hash)
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('redirected');
      const newSearch = urlParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }

  /** Remove prefixes from translation keys (generic., orgFe., patientApp., patientApp.editor.) */
  static removeTranslationPrefixes(translations: Record<string, string>): Record<string, string> {
    const processed: Record<string, string> = {};
    const prefixes = ['patientApp.', 'orgFe.', 'generic.'];

    for (const [key, value] of Object.entries(translations)) {
      let processedKey = key;
      // Remove the longest matching prefix first
      for (const prefix of prefixes) {
        if (processedKey.startsWith(prefix)) {
          processedKey = processedKey.substring(prefix.length);
          break;
        }
      }
      processed[processedKey] = value;
    }

    return processed;
  }

  static isUrl(input: string): boolean {
    try {
      const url = new URL(input);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_e) {
      return false;
    }
  }

  static isMedudocForm(formGroup: FormStage2SummaryItem[]): boolean {
    return formGroup.some((f) => f.source === 'medudoc');
  }
}
